import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Dropdown, Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ROLE_TYPES, canUseViewAs, isWarehouseRole } from '../../constants/roleConstants';
import './DashboardFilters.css';

const VIEW_AS_OPTIONS = [
    { label: 'Admin View', value: ROLE_TYPES.ADMIN },
    { label: 'Seller View', value: ROLE_TYPES.SELLER },
    { label: 'Warehouse Manager View', value: ROLE_TYPES.WAREHOUSE_MANAGER },
    { label: 'Warehouse Worker View', value: ROLE_TYPES.WAREHOUSE_WORKER },
    { label: 'Super Admin View', value: ROLE_TYPES.SUPER_ADMIN }
];

const DashboardFilters = ({
    filters,
    onFilterChange,
    companies,
    sellers,
    warehouses,
    onCompanyChange,
    companiesLoading,
    companiesError,
    presets,
    selectedPresetId,
    onPresetChange,
    canModifySelectedPreset,
    onCreateDashboard,
    onEditDashboard,
    onDeleteDashboard,
    onCustomizeWidgets,
    onResetLayout,
    onExportLayout,
    onImportLayout,
    onExportPdf,
    isExportingPdf,
    lockCompanyFilter,
    lockSellerFilter,
    userRole,
    viewAsRole,
    onViewAsChange,
    onApplyClick,
    hasPendingChanges
}) => {
    const [localFilters, setLocalFilters] = useState({
        ...filters,
        fromDate: filters?.fromDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        toDate: filters?.toDate || new Date()
    });
    const isCustomRange = localFilters.period === 'Custom Range';
    const selectedCompanyValue = lockCompanyFilter ? (localFilters.companyId || companies?.[0]?.Id || '') : (localFilters.companyId || '');
    const selectedSellerValue = lockSellerFilter ? (localFilters.sellerId || sellers?.[0]?.id || '') : (localFilters.sellerId || '');

    const showWarehouseFilter = isWarehouseRole(userRole);

    // Sync local filters with parent filters prop
    useEffect(() => {
        if (filters) {
            setLocalFilters({
                ...filters,
                fromDate: filters?.fromDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                toDate: filters?.toDate || new Date()
            });
        }
    }, [filters]);

    const handleChange = (field, value) => {
        setLocalFilters(prev => {
            const nextFilters = { ...prev, [field]: value };
            onFilterChange(nextFilters);
            return nextFilters;
        });
    };

    const handleCompanyChange = (companyId) => {
        const id = companyId ? Number.parseInt(companyId, 10) : null;
        const nextFilters = { ...localFilters, companyId: id, sellerId: null };
        setLocalFilters(nextFilters);
        onFilterChange(nextFilters);
        
        // Notify parent to fetch sellers for this company
        if (onCompanyChange && id) {
            onCompanyChange(id);
        }
    };

    const handlePeriodChange = (period) => {
        const today = new Date();
        let fromDate = new Date(today);
        let toDate = new Date(today);
        
        switch (period) {
            case 'Today':
                fromDate = new Date(today);
                toDate = new Date(today);
                break;
            case 'Last 7D':
                fromDate = new Date(today);
                fromDate.setDate(today.getDate() - 6);
                toDate = new Date(today);
                break;
            case 'This Month':
                fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
                toDate = new Date(today);
                break;
            case 'Last Month':
                fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                toDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'Last 3 Month':
                fromDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
                toDate = new Date(today);
                break;
            case 'Custom Range':
                setLocalFilters(prev => {
                    const nextFilters = { ...prev, period };
                    onFilterChange(nextFilters);
                    return nextFilters;
                });
                return;
            default:
                fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        }

        setLocalFilters(prev => {
            const nextFilters = { ...prev, period, fromDate, toDate };
            onFilterChange(nextFilters);
            return nextFilters;
        });
    };

    return (
        <div className="dashboard-filters">
            <Row className="align-items-end g-3">
                <Col xl={2} md={4} sm={6}>
                    <Form.Group>
                        <Form.Label>Period</Form.Label>
                        <Form.Control
                            as="select"
                            value={localFilters.period}
                            onChange={(e) => handlePeriodChange(e.target.value)}
                        >
                            <option value="Today">Today</option>
                            <option value="Last 7D">Last 7 Days</option>
                            <option value="This Month">This Month</option>
                            <option value="Last Month">Last Month</option>
                            <option value="Last 3 Month">Last 3 Month</option>
                            <option value="Custom Range">Custom Range</option>
                        </Form.Control>
                    </Form.Group>
                </Col>

                <Col xl={2} md={4} sm={6}>
                    <Form.Group>
                        <Form.Label>From Date</Form.Label>
                        <DatePicker
                            selected={localFilters.fromDate}
                            onChange={(date) => handleChange('fromDate', date)}
                            className="form-control"
                            dateFormat="MM/dd/yyyy"
                            maxDate={new Date()}
                            disabled={!isCustomRange}
                        />
                    </Form.Group>
                </Col>
                <Col xl={2} md={4} sm={6}>
                    <Form.Group>
                        <Form.Label>To Date</Form.Label>
                        <DatePicker
                            selected={localFilters.toDate}
                            onChange={(date) => handleChange('toDate', date)}
                            className="form-control"
                            dateFormat="MM/dd/yyyy"
                            maxDate={new Date()}
                            minDate={localFilters.fromDate}
                            disabled={!isCustomRange}
                        />
                    </Form.Group>
                </Col>

                <Col xl={2} md={4} sm={6}>
                    <Form.Group>
                        <Form.Label>Company</Form.Label>
                        <Form.Control
                            as="select"
                            value={selectedCompanyValue}
                            onChange={(e) => handleCompanyChange(e.target.value)}
                            disabled={companiesLoading || lockCompanyFilter}
                        >
                            {(() => {
                                if (companiesLoading) {
                                    return <option value="">Loading...</option>;
                                }
                                if (companiesError) {
                                    return <option value="">Error loading companies</option>;
                                }
                                return (
                                    <>
                                        {!lockCompanyFilter && <option value="">All Companies</option>}
                                        {companies?.map(company => (
                                            <option key={company.Id} value={company.Id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </>
                                );
                            })()}
                        </Form.Control>
                    </Form.Group>
                </Col>

                <Col xl={2} md={4} sm={6}>
                    <Form.Group>
                        <Form.Label>Seller</Form.Label>
                        <Form.Control
                            as="select"
                            value={selectedSellerValue}
                            onChange={(e) => handleChange('sellerId', e.target.value ? Number.parseInt(e.target.value, 10) : null)}
                            disabled={lockSellerFilter || !localFilters.companyId}
                        >
                            {!lockSellerFilter && <option value="">All Sellers</option>}
                            {sellers?.map(seller => (
                                <option key={seller.id} value={seller.id}>
                                    {seller.newSellerName || seller.sellerCode}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                </Col>

                {showWarehouseFilter && (
                    <Col xl={2} md={4} sm={6}>
                        <Form.Group>
                            <Form.Label>Warehouse</Form.Label>
                            <Form.Control
                                as="select"
                                value={localFilters.warehouseId || ''}
                                onChange={(e) => handleChange('warehouseId', e.target.value ? Number.parseInt(e.target.value, 10) : null)}
                            >
                                <option value="">All Warehouses</option>
                                {warehouses?.map(warehouse => (
                                    <option key={warehouse.id} value={warehouse.id}>
                                        {warehouse.name}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                )}

                {canUseViewAs(userRole) && (
                    <Col xl={2} md={4} sm={6}>
                        <Form.Group>
                            <Form.Label>View As</Form.Label>
                            <Form.Control
                                as="select"
                                value={viewAsRole !== null && viewAsRole !== undefined ? viewAsRole : userRole}
                                onChange={(e) => onViewAsChange?.(Number.parseInt(e.target.value, 10))}
                                disabled
                                title="Locked to Seller View until other role views are ready"
                            >
                                {VIEW_AS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                )}

                <Col xl={2} md={4} sm={6}>
                    <Form.Group>
                        <Form.Label>Select Dashboard</Form.Label>
                        <div className="dashboard-selector-with-settings">
                            <Form.Control
                                as="select"
                                value={selectedPresetId || ''}
                                onChange={onPresetChange}
                            >
                                {(!presets || presets.length === 0) && (
                                    <option value="">Default Dashboard</option>
                                )}
                                {presets?.map((preset) => (
                                    <option key={preset.Id} value={preset.Id}>
                                        {preset.Name}
                                    </option>
                                ))}
                            </Form.Control>

                            <Dropdown align="end">
                                <Dropdown.Toggle variant="light" id="dashboard-actions-filter" className="dashboard-filter-settings-toggle" aria-label="Dashboard settings">
                                    {'\u2699'}
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={onCreateDashboard}>Create Dashboard</Dropdown.Item>
                                    <Dropdown.Item onClick={onEditDashboard} disabled={!canModifySelectedPreset}>Edit Dashboard</Dropdown.Item>
                                    <Dropdown.Item onClick={onDeleteDashboard} disabled={!canModifySelectedPreset}>Delete Dashboard</Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={onCustomizeWidgets}>Customize Widgets</Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={onResetLayout}>Reset to Default Layout</Dropdown.Item>
                                    <Dropdown.Item onClick={onExportLayout}>Export Layout JSON</Dropdown.Item>
                                    <Dropdown.Item onClick={onImportLayout}>Import Layout JSON</Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={onExportPdf} disabled={isExportingPdf}>{isExportingPdf ? 'Exporting PDF...' : 'Export as PDF'}</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </Form.Group>
                </Col>

                <Col xl={2} md={4} sm={6} className="ml-auto ms-auto dashboard-filters__apply-col">
                    <Form.Group>
                        <Form.Label>&nbsp;</Form.Label>
                        <Button
                            variant="primary"
                            className="w-100 btn-apply"
                            onClick={onApplyClick}
                            disabled={!hasPendingChanges}
                        >
                            Apply
                        </Button>
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
};

DashboardFilters.propTypes = {
    filters: PropTypes.object.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    companies: PropTypes.array,
    sellers: PropTypes.array,
    warehouses: PropTypes.array,
    onCompanyChange: PropTypes.func,
    companiesLoading: PropTypes.bool,
    companiesError: PropTypes.string,
    presets: PropTypes.array,
    selectedPresetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onPresetChange: PropTypes.func,
    canModifySelectedPreset: PropTypes.bool,
    onCreateDashboard: PropTypes.func,
    onEditDashboard: PropTypes.func,
    onDeleteDashboard: PropTypes.func,
    onCustomizeWidgets: PropTypes.func,
    onResetLayout: PropTypes.func,
    onExportLayout: PropTypes.func,
    onImportLayout: PropTypes.func,
    onExportPdf: PropTypes.func,
    isExportingPdf: PropTypes.bool,
    lockCompanyFilter: PropTypes.bool,
    lockSellerFilter: PropTypes.bool,
    userRole: PropTypes.number,
    viewAsRole: PropTypes.number,
    onViewAsChange: PropTypes.func,
    onApplyClick: PropTypes.func,
    hasPendingChanges: PropTypes.bool
};

DashboardFilters.defaultProps = {
    companies: [],
    sellers: [],
    warehouses: [],
    onCompanyChange: null,
    companiesLoading: false,
    companiesError: null,
    presets: [],
    selectedPresetId: '',
    onPresetChange: null,
    canModifySelectedPreset: false,
    onCreateDashboard: null,
    onEditDashboard: null,
    onDeleteDashboard: null,
    onCustomizeWidgets: null,
    onResetLayout: null,
    onExportLayout: null,
    onImportLayout: null,
    onExportPdf: null,
    isExportingPdf: false,
    lockCompanyFilter: false,
    lockSellerFilter: false,
    userRole: ROLE_TYPES.SELLER,
    viewAsRole: null,
    onViewAsChange: null,
    onApplyClick: null,
    hasPendingChanges: false
};

export default DashboardFilters;
